import { useCallback, useEffect, useMemo, useState } from "react";

import dayjs from "@/lib/dayjs";

import * as Model from "../../model";

type Props = {
	panelChildren: React.ReactNode;
	firstOptionStartAt: string | null;
	lastOptionEndAt: string | null;
	searchDate: string | null;
	// パネル領域の最小幅（時間グリッド線がパネル右端まで届くように動的計算した値）
	panelAreaMinWidth: number;
};

const useCurrentTimePosition = (firstOptionStartAt: string | null, searchDate: string | null) => {
	const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(0);

	const isToday = useMemo(() => {
		if (!searchDate) return false;

		return dayjs(searchDate).isSame(dayjs(), "day");
	}, [searchDate]);

	// 現在時刻の位置を計算する
	const calculateCurrentTimePosition = useCallback(() => {
		const now = new Date();
		const hours = now.getHours();
		const minutes = now.getMinutes();

		if (!firstOptionStartAt || !isToday) return null;

		const [firstHour] = firstOptionStartAt.split(":");
		const firstHourNum = Number(firstHour);

		// 現在時刻の位置を計算（時間 * 70 + 分）
		return (hours - firstHourNum) * Model.Constants.PerOneHour + minutes;
	}, [firstOptionStartAt, isToday]);

	// 1分ごとに現在時刻の位置を更新
	useEffect(() => {
		const updatePosition = () => {
			setCurrentTimePosition(calculateCurrentTimePosition());
		};

		// 初回実行
		updatePosition();

		// 10分ごとに更新
		const interval = setInterval(updatePosition, 60000 * 10);

		return () => clearInterval(interval);
	}, [calculateCurrentTimePosition, isToday]);

	return { currentTimePosition };
};

export const Timeline = ({ panelChildren, firstOptionStartAt, lastOptionEndAt, searchDate, panelAreaMinWidth }: Props) => {
	const { currentTimePosition } = useCurrentTimePosition(firstOptionStartAt, searchDate);

	const excludedTimeList = useMemo(() => {
		if (!firstOptionStartAt || !lastOptionEndAt) {
			return Model.Constants.TimeList;
		}

		return Model.Constants.TimeList.filter(({ time }, idx, array) => {
			const [firstOptionStartAtHour] = firstOptionStartAt.split(":");
			const [lastOptionEndAtHour] = lastOptionEndAt.split(":");
			const [currentHour] = time.split(":");
			const [nextHour] = array[idx + 1]?.time.split(":") ?? [];

			// 開始時間や終了時間が 07:30 だった場合 07:00 ~ or  ~ 08:00 を配列に含める
			const addedLastOptionEndAtHour = currentHour === lastOptionEndAtHour ? nextHour : lastOptionEndAtHour;

			return firstOptionStartAtHour <= currentHour && currentHour <= addedLastOptionEndAtHour;
		});
	}, [firstOptionStartAt, lastOptionEndAt]);

	const excludedHourList = useMemo(() => {
		return Model.Constants.HourList.filter(({ hour }) => {
			return excludedTimeList.map((item) => item.id).includes(hour);
		});
	}, [excludedTimeList]);

	return (
		<div className="min-w-lg w-full overflow-y-hidden">
			<div className="relative flex h-full gap-x-2 overflow-y-scroll">
				{/* 時間軸（横スクロール時も常に左端に残す） */}
				<div className="sticky left-0 z-20 bg-white pr-2">
					<ul className="m-0 flex flex-col items-end p-0 [&>li]:min-h-[70px]">
						{excludedTimeList.map((time) => {
							const splitHour = Number(time.time.split(":")[0]);
							const timeText = `${splitHour}:${time.time.split(":")[1]}`;

							return <li key={time.id}>{timeText}</li>;
						})}
					</ul>
				</div>

				<div
					className="relative flex w-full overflow-y-hidden"
					style={{ minWidth: `${panelAreaMinWidth}px` }}
				>
					<div className="relative top-[10px] flex min-w-[99.95%]">
						<div className="grid w-full grid-cols-1 border-t  border-zinc-200">
							<div className="z-1 relative w-full border-r-zinc-200">
								{excludedHourList.map((hour) => {
									return (
										<div
											key={hour.hour}
											className="block h-[70px] w-full border-b border-b-zinc-200"
										/>
									);
								})}

								{/* 現在時刻を示す赤い線 */}
								<div
									className="absolute left-0 right-0 z-10 border-t-2 border-brand opacity-80"
									style={currentTimePosition ? { top: `${currentTimePosition}px` } : {}}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* パネルの描画 */}
				{panelChildren}
			</div>
		</div>
	);
};
